
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import 'lodash/isEqual';
import 'just-kebab-case';
import { l as labelsMixin } from './labels-mixin.js';
import InputNumber from 'element-ui/lib/input-number';
import debounce from 'lodash/debounce';

const UNIT_FACTORS_MAP = {
  dp: 0.88,
  px: 1
};

function decimalAdjust(type, value, exp) {
  if (typeof exp === 'undefined' || +exp === 0) {
    return Math[type](value);
  }

  value = +value;
  exp = +exp;

  if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
    return NaN;
  }

  value = value.toString().split('e');
  value = Math[type](+`${value[0]}e${value[1] ? +value[1] - exp : -exp}`);
  value = value.toString().split('e');
  return +`${value[0]}e${value[1] ? +value[1] + exp : exp}`;
}

function formatOutputValue({
  value,
  precision = 0,
  unit
}) {
  if (!unit) {
    return value;
  }

  const outputValue = decimalAdjust('floor', value / (UNIT_FACTORS_MAP[unit] || 1), -precision);
  return `${outputValue}${unit}`;
}
function parseIncomingUnit({
  value
}) {
  return String(value).match(/[\d.\-\+]*\s*(.*)/)[1] || '';
}
function parseIncomingValue({
  value,
  precision = 0
}) {
  const str = String(value);
  const num = parseFloat(str, 10);
  const unit = str.match(/[\d.\-\+]*\s*(.*)/)[1] || '';

  if (UNIT_FACTORS_MAP[unit]) {
    return decimalAdjust('ceil', num * UNIT_FACTORS_MAP[unit], -precision);
  }

  return num;
}

var script = {
  name: 'PendoInputNumber',
  components: {
    ElInputNumber: InputNumber
  },
  mixins: [labelsMixin],
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    name: {
      type: String,
      default: ''
    },
    step: {
      type: Number,
      default: 1
    },
    controls: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    min: {
      type: Number,
      default: -Infinity
    },
    max: {
      type: Number,
      default: Infinity
    },
    value: {
      type: [Number, String],
      default: null
    },
    size: {
      type: String,
      default: 'medium'
    },
    controlsPosition: {
      type: String,
      default: 'right'
    },
    precision: {
      type: Number,
      default: null
    },
    inputWidth: {
      type: [String, Number],
      default: null
    },
    alwaysShowArrows: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      unit: null,
      isFocused: false,
      isArrowDownActive: false,
      isArrowUpActive: false
    };
  },

  computed: {
    model() {
      if (typeof this.value === 'string') {
        return parseIncomingValue({
          value: this.value,
          precision: this.precision
        });
      }

      return this.value;
    },

    inputWidthValue() {
      if (typeof this.inputWidth === 'string') {
        return this.inputWidth;
      }

      return `${this.inputWidth}px`;
    }

  },

  created() {
    if (typeof this.value === 'string') {
      this.unit = parseIncomingUnit({
        value: this.value
      });
    }
  },

  methods: {
    appendInputSuffixLabel() {
      if (!this.suffixLabel) {
        return;
      }

      const node = document.createElement('span');
      node.classList.add('el-input-number__suffix-label');
      const textnode = document.createTextNode(this.suffixLabel);
      node.appendChild(textnode);
      this.$refs.elInputNumber.$el.prepend(node);
    },

    handleArrowUpKeydown() {
      this.isArrowUpActive = true;
    },

    handleArrowDownKeydown() {
      this.isArrowDownActive = true;
    },

    handleArrowUpKeyup: debounce(function () {
      this.isArrowUpActive = false;
    }, 250),
    handleArrowDownKeyup: debounce(function () {
      this.isArrowDownActive = false;
    }, 250),

    handleFocus(event) {
      this.isFocused = true;
      this.$emit('focus', event);
    },

    handleBlur(event) {
      this.isFocused = false;
      this.$emit('blur', event);
    },

    handleChange(value) {
      if (this.unit) {
        const unit = this.unit,
              precision = this.precision;
        return this.$emit('change', formatOutputValue({
          value,
          precision,
          unit
        }));
      }

      return this.$emit('change', value);
    }

  }
};

const __vue_script__ = script;

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "pendo-input-number",
    class: {
      'pendo-input-number--inline-label': !!_vm.inlineLabel
    }
  }, [_vm.hasTopLabel ? _c('div', {
    staticClass: "pendo-input-number__label pendo-input-number__label--top"
  }, [_vm._t("topLabel", [_vm._v("\n            " + _vm._s(_vm.topLabel) + "\n        ")])], 2) : _vm._e(), _vm._v(" "), _c('el-input-number', {
    ref: "elInputNumber",
    class: {
      'is-focused': _vm.isFocused,
      'is-arrow-up-active': _vm.isArrowUpActive,
      'is-arrow-down-active': _vm.isArrowDownActive,
      'always-show-arrows': _vm.alwaysShowArrows
    },
    style: {
      width: _vm.inputWidthValue
    },
    attrs: {
      "value": _vm.model,
      "name": _vm.name,
      "label": _vm.label,
      "disabled": _vm.disabled,
      "min": _vm.min,
      "max": _vm.max,
      "controls": _vm.controls,
      "controls-position": _vm.controlsPosition,
      "size": _vm.size,
      "precision": _vm.precision,
      "step": _vm.step
    },
    on: {
      "hook:mounted": _vm.appendInputSuffixLabel,
      "change": _vm.handleChange,
      "blur": _vm.handleBlur,
      "focus": _vm.handleFocus
    },
    nativeOn: {
      "keydown": [function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "arrow-up", undefined, $event.key, undefined)) {
          return null;
        }

        return _vm.handleArrowUpKeydown($event);
      }, function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "arrow-down", undefined, $event.key, undefined)) {
          return null;
        }

        return _vm.handleArrowDownKeydown($event);
      }],
      "keyup": [function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "arrow-up", undefined, $event.key, undefined)) {
          return null;
        }

        return _vm.handleArrowUpKeyup($event);
      }, function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "arrow-down", undefined, $event.key, undefined)) {
          return null;
        }

        return _vm.handleArrowDownKeyup($event);
      }]
    }
  }), _vm._v(" "), _vm.hasBottomLabel ? _c('div', {
    staticClass: "pendo-input-number__label pendo-input-number__label--bottom"
  }, [_vm._t("bottomLabel", [_vm._v("\n            " + _vm._s(_vm.bottomLabel) + "\n        ")])], 2) : _vm._e()], 1);
};

var __vue_staticRenderFns__ = [];
const __vue_inject_styles__ = undefined;
const __vue_scope_id__ = undefined;
const __vue_is_functional_template__ = false;

function __vue_normalize__(template, style, script, scope, functional, moduleIdentifier, createInjector, createInjectorSSR) {
  const component = (typeof script === 'function' ? script.options : script) || {};
  component.__file = "pendo-input-number.vue";

  if (!component.render) {
    component.render = template.render;
    component.staticRenderFns = template.staticRenderFns;
    component._compiled = true;
    if (functional) component.functional = true;
  }

  component._scopeId = scope;

  return component;
}

var PendoInputNumber = __vue_normalize__({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__);

export default PendoInputNumber;
